import { MatDialog } from '@angular/material/dialog';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AlertService } from 'src/app/services/alert.service';
import { FileUploadModalService } from 'src/app/services/file-upload-modal.service';
import {
  IpcChannel,
  IpcResponse,
  IpcV2Service,
} from 'src/app/services/ipc-v2.service';
import { environment } from 'src/environments/environment';
import { ErrorComponent } from './error/error.component';

enum DownloadStatus {
  NOT_STARTED = 'not-started',
  DOWNLOADING = 'downloading',
  COMPLETED = 'completed',
  NOT_AVAILABLE = 'not-available',
  ALREADY_UP_TO_DATE = 'already-up-to-date',
  ERROR = 'error',
}

interface DownloadProgress {
  progress: number;
  status: DownloadStatus;
}

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
})
export class MainComponent implements AfterViewInit, OnDestroy {
  version = null;
  updateAvailable = false;
  downloadProgress: DownloadProgress = {
    progress: 0,
    status: DownloadStatus.NOT_STARTED,
  };
  readonly DownloadStatus = DownloadStatus;

  updateSub!: Subscription;
  isUpdateDownloaded = false;
  logger = false;
  constructor(
    public dialog: MatDialog,
    public fileUploadModalService: FileUploadModalService,
    private readonly ref: ChangeDetectorRef,
    private readonly als: AlertService,
    private readonly ipcV2: IpcV2Service,
  ) {}

  ngAfterViewInit(): void {
    console.log('Setting up update check listener');

    this.ipcV2.sendAndReceive(IpcChannel.GET_VERSION).subscribe(
      (res) => {
        this.version = res.data;
        this.checkForUpdates(); // Only check for updates after getting version
      },
      (error) => {},
    );
  }

  isElectron() {
    return this.ipcV2.isElectron();
  }

  checkForUpdates() {
    console.log('Checking for updates and downloading if available...');
    this.downloadProgress = {
      progress: 0,
      status: DownloadStatus.NOT_STARTED,
    };
    this.ref.detectChanges();
    this.downloadUpdate();
  }

  downloadUpdate() {
    if (this.downloadProgress.status === DownloadStatus.DOWNLOADING) {
      console.log('Download already in progress, skipping...');
      return;
    }

    console.log('Starting update check and download...');
    this.downloadProgress = { progress: 0, status: DownloadStatus.DOWNLOADING };
    this.ref.detectChanges();

    this.updateSub = this.ipcV2
      .sendAndReceive(IpcChannel.DOWNLOAD_IF_UPDATE_AVAILABLE)
      .subscribe({
        next: (response) => {
          console.log('📥 Download progress update:', response.data);
          this.downloadProgress = {
            progress: response.data.progress || 0,
            status: response.data.status as DownloadStatus,
          };
          this.ref.detectChanges();

          if (response.data.status === 'completed') {
            this.isUpdateDownloaded = true;
            this.updateAvailable = true;
            console.log('Download completed! Ready to restart.');
          }

          if (response.data.status === 'not-available') {
            this.updateAvailable = false;
            console.log('No update available');
          }
        },
        error: (err) => {
          console.error('Download error:', err);
          this.downloadProgress = {
            progress: 0,
            status: DownloadStatus.ERROR,
          };
          this.ref.detectChanges();
        },
      });
  }

  searchRecord() {
    this.ipcV2
      .sendAndReceive(IpcChannel.OPEN_URL, environment.domain_login)
      .subscribe(
        (res) => {
          console.log('Opening URL');
        },
        (error) => {
          window.open(environment.domain_login, '_blank');
        },
      );
  }

  async restartApp() {
    try {
      console.log(
        '🔄 Restart button clicked, current status:',
        this.downloadProgress.status,
      );

      if (this.downloadProgress.status === DownloadStatus.COMPLETED) {
        console.log('Update downloaded, initiating restart...');

        this.ipcV2.sendAndReceive(IpcChannel.RESTART_APP).subscribe({
          next: (response) => {
            console.log('Restart response:', response);
          },
          error: (err) => {
            console.error('Restart failed:', err);
          },
        });
      } else {
        console.warn(
          'Cannot restart - update not completed. Current status:',
          this.downloadProgress.status,
        );
      }
    } catch (err) {
      console.error('Failed to restart app:', err);
    }
  }

  openLogViewer() {
    this.dialog.open(ErrorComponent, {
      width: '900px',
      maxHeight: '80vh',
      data: null,
    });
  }

  onContextMenu(event: MouseEvent) {
    event.preventDefault();
    this.ipcV2.send(IpcChannel.SHOW_CONTEXT_MENU);
  }

  ngOnDestroy(): void {
    this.ipcV2.removeListener(IpcChannel.UPDATE_AVAILABLE);
    this.ipcV2.removeListener(IpcChannel.GET_VERSION);
    this.ipcV2.removeListener(IpcChannel.DOWNLOAD_IF_UPDATE_AVAILABLE);
    this.ipcV2.removeListener(IpcChannel.RESTART_APP);
    this.ipcV2.removeListener(IpcChannel.GET_LOGS);
  }

  parseInt(value: string) {
    return parseInt(value);
  }
}
